// apps/web/src/lib/painel/home/lista.ts — Fase 17.8, Plano 07 (D-09; UI-SPEC U-05, U-12 e Copywriting Contract).
// Decisões de apresentação da lista "Precisa de você" em funções puras (o componente fica fino): o texto do "quando" de cada
// linha, a contagem do cabeçalho, o prefixo oculto de urgência e o rótulo do botão "Ver todos". Todo cálculo de dia usa o fuso
// operacional (America/Sao_Paulo), nunca o do navegador.
import type { QuandoItem, UrgenciaItem } from '@pieltec/contracts';
import { diaOperacional, TIMEZONE_OPERACIONAL } from '../../dia-operacional';
import { formatarNumero } from './formato';

/** Linhas visíveis com a lista recolhida (U-05). */
export const LIMITE_LINHAS_RECOLHIDA = 5;

const MS_MINUTO = 60_000;
const MS_HORA = 3_600_000;

const FORMATO_DATA = new Intl.DateTimeFormat('pt-BR', { timeZone: TIMEZONE_OPERACIONAL, day: 'numeric', month: 'short' });

const plural = (n: number, singular: string, pluralTexto: string): string => `${formatarNumero(n)} ${n === 1 ? singular : pluralTexto}`;

/** `há 25 min`, `há 26 h` (até 47 h) e `há 3 dias`; menos de 1 min (ou relógio adiantado) é `agora`. */
function haQuanto(decorridoMs: number): string {
  if (decorridoMs < MS_MINUTO) return 'agora';
  if (decorridoMs < MS_HORA) return `há ${Math.floor(decorridoMs / MS_MINUTO)} min`;
  if (decorridoMs < 48 * MS_HORA) return `há ${Math.floor(decorridoMs / MS_HORA)} h`;
  return `há ${plural(Math.floor(decorridoMs / (24 * MS_HORA)), 'dia', 'dias')}`;
}

const DATA_PURA = /^\d{4}-\d{2}-\d{2}$/;

/**
 * Dia de calendário (`AAAA-MM-DD`) do prazo. O contrato manda o prazo como data pura e ela é lida como está: nenhum fuso
 * entra (00:00 UTC lido como instante recuaria o dia em São Paulo: D5 da UAT). Um instante completo (contrato antigo) vira o
 * dia de São Paulo. Ilegível devolve `null`.
 */
function diaDoPrazo(iso: string): string | null {
  if (DATA_PURA.test(iso)) return Number.isNaN(Date.parse(`${iso}T00:00:00Z`)) ? null : iso;
  const instante = new Date(iso);
  return Number.isNaN(instante.getTime()) ? null : diaOperacional(instante);
}

/** `AAAA-MM-DD` para `DD/MM`. */
const diaMes = (dia: string): string => `${dia.slice(8, 10)}/${dia.slice(5, 7)}`;

/** Texto do prazo (data de calendário, sem hora): `vence hoje`, `vence amanhã`, `vence em 16/10`, `venceu ontem`, `venceu há 3 dias`. */
function rotuloPrazo(iso: string, agora: Date): string {
  const dia = diaDoPrazo(iso);
  if (dia === null) return '';
  const dias = Math.round((Date.parse(`${dia}T00:00:00Z`) - Date.parse(`${diaOperacional(agora)}T00:00:00Z`)) / (24 * MS_HORA));
  if (dias === 0) return 'vence hoje';
  if (dias === 1) return 'vence amanhã';
  if (dias > 1) return `vence em ${diaMes(dia)}`;
  if (dias === -1) return 'venceu ontem';
  return `venceu há ${plural(-dias, 'dia', 'dias')}`;
}

/**
 * Texto do "quando" de uma linha:
 *  - PRAZO (data de calendário, nunca com hora): `vence hoje`, `vence amanhã`, `vence em 16/10`; vencido: `venceu ontem`,
 *    `venceu há 3 dias`;
 *  - DESDE: `há 25 min`, `há 26 h`, `há 3 dias`;
 *  - DATA: `13 de out.`.
 * Data ou instante ilegível devolve vazio: a linha simplesmente não mostra o "quando".
 */
export function rotuloQuando(quando: QuandoItem, agora: Date): string {
  if (quando.tipo === 'PRAZO') return rotuloPrazo(quando.iso, agora);
  const instante = new Date(quando.iso);
  if (Number.isNaN(instante.getTime())) return '';
  if (quando.tipo === 'DATA') return FORMATO_DATA.format(instante);
  return haQuanto(agora.getTime() - instante.getTime());
}

/** `1 item` / `4 itens` (contagem do cabeçalho do painel). */
export function rotuloContagem(n: number): string {
  return plural(n, 'item', 'itens');
}

/** Prefixo oculto lido pelo leitor de tela antes do "quando": a cor nunca é o único sinal de urgência. */
export function prefixoUrgencia(urgencia: UrgenciaItem): string {
  if (urgencia === 'CRITICA') return 'Urgente: ';
  if (urgencia === 'ATENCAO') return 'Atenção: ';
  return '';
}

/** Rótulo do botão que expande a lista no próprio painel. */
export function rotuloVerTodos(total: number, expandido: boolean): string {
  return expandido ? 'Mostrar menos' : `Ver todos (${formatarNumero(total)})`;
}
